/**
 * One library — Live TV, Movies or Series.
 *
 * The library is a route parameter and goes straight into the API path, so there is one
 * component for three tabs and no code path that can merge them.
 *
 * ## Two modes, and why the default changed
 *
 * This screen used to open on a category rail and a grid: a few hundred provider names
 * like "TR : AKSIYON FILMLER | VOD |" followed by everything at once. That is the shape of
 * the subscription, not the shape of a catalog, and it makes finding something a memory
 * exercise.
 *
 * It now opens on discovery rows and keeps the old view intact behind **Tüm Kategoriler**.
 * The provider categories are not hidden or replaced — some of them are genuinely the
 * best way to find a thing, and a household that has learned where its films live should
 * not have that taken away. They are simply no longer the first thing you meet.
 *
 * Searching or picking a filter switches to the grid, because a result set is a grid: a
 * rail that scrolls sideways through 900 matches is a worse answer than a page of them.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CategoryDto, DiscoveryDto, ContinueWatchingDto } from '@filmodasi/shared';
import { useApi, usePagedCatalog } from '../api/hooks';
import { ErrorBox, Empty, ItemGrid, SkeletonGrid, SkeletonRails } from '../components/ui';
import { DiscoveryRow } from '../components/rows';

const TITLES: Record<string, string> = {
  live: 'Canlı TV',
  movies: 'Filmler',
  series: 'Diziler',
};

export type LibrarySegment = 'live' | 'movies' | 'series';

/** What the grid is currently showing. `null` in every field means "everything". */
interface GridFilter {
  categoryId?: string;
  genre?: string;
  language?: string;
  label: string;
}

export function LibraryPage({ library }: { library: LibrarySegment }) {
  const [filter, setFilter] = useState<GridFilter | null>(null);
  const [query, setQuery] = useState('');
  const [applied, setApplied] = useState('');
  // Off by default: Chrome plays .mkv, so hiding it automatically would remove most of
  // the catalog from the users who can actually watch it.
  const [playableOnly, setPlayableOnly] = useState(false);
  const [browsing, setBrowsing] = useState(false);

  // A search or a chosen filter is a grid question; anything else opens on the rows.
  const gridMode = browsing || applied !== '' || filter !== null;

  const discovery = useApi<DiscoveryDto & { continueWatching: ContinueWatchingDto[] }>(
    gridMode ? null : `/api/discovery/${library}`,
    [library],
  );
  const categories = useApi<CategoryDto[]>(
    gridMode ? `/api/catalog/${library}/categories` : null,
    [library, gridMode],
  );

  const params: Record<string, string> = { sort: library === 'live' ? 'title' : 'newest' };
  if (filter?.categoryId) params.categoryId = filter.categoryId;
  if (filter?.genre) params.genre = filter.genre;
  if (filter?.language) params.language = filter.language;
  if (applied) params.q = applied;
  if (playableOnly) params.playableOnly = 'true';

  const grid = usePagedCatalog(gridMode ? `/api/catalog/${library}` : '', params);

  const backToRows = () => {
    setFilter(null);
    setApplied('');
    setQuery('');
    setBrowsing(false);
  };

  /*
   * Changing tab resets the mode.
   *
   * Live, Movies and Series are one component with a changed prop, so React keeps the
   * instance and every piece of state with it: browsing Turkish action films and then
   * clicking Diziler landed on the series *grid*, filtered by a category that belongs to
   * another library. A library is supposed to open on its rows — that is the entire point
   * of the screen — and "open on whatever the last library was doing" is not that.
   */
  useEffect(() => {
    setBrowsing(false);
    setFilter(null);
    setApplied('');
    setQuery('');
  }, [library]);

  return (
    <main className="library">
      <header className="library__header">
        <h1>{TITLES[library] ?? library}</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setApplied(query.trim());
          }}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${TITLES[library] ?? ''} içinde ara`}
          />
        </form>

        {gridMode ? (
          <button type="button" className="library__mode" onClick={backToRows}>
            ← Keşfet
          </button>
        ) : (
          <button type="button" className="library__mode" onClick={() => setBrowsing(true)}>
            Tüm Kategoriler
          </button>
        )}

        {library !== 'live' && gridMode && (
          <label className="toggle">
            <input
              type="checkbox"
              checked={playableOnly}
              onChange={(e) => setPlayableOnly(e.target.checked)}
            />
            Yalnız mp4 (her tarayıcıda oynar)
          </label>
        )}
      </header>

      {!gridMode && (
        <>
          {discovery.error && <ErrorBox message={discovery.error} onRetry={discovery.reload} />}
          {/* Rails, not a spinner — the same reasoning as Home. The shape follows the
              library, because a live rail is wide logo tiles and a poster placeholder under
              them would jump twice: once in width, once in height. */}
          {discovery.loading && !discovery.data && (
            <>
              <p className="sr-only" role="status">
                Yükleniyor…
              </p>
              <SkeletonRails poster={library !== 'live'} />
            </>
          )}

          {discovery.data?.rows.map((row) => (
            <DiscoveryRow
              key={row.key}
              row={row}
              continueWatching={discovery.data?.continueWatching}
              onSeeAll={(seeAll) =>
                setFilter({
                  genre: seeAll.genre,
                  language: seeAll.language,
                  // The row already worked out what to call itself; repeating that
                  // mapping here would be a second table to keep in step.
                  label: row.title,
                })
              }
            />
          ))}

          {discovery.data && discovery.data.rows.length === 0 && (
            <Empty title="Bu kütüphane boş" action={<Link to="/settings">Ayarlara git</Link>}>
              Yayın aboneliğinizi ekleyip eşitledikten sonra burası dolar.
            </Empty>
          )}
        </>
      )}

      {gridMode && (
        <>
          {filter && (
            /*
             * The button names where it goes, not where you are.
             *
             * It read "← Haber Kanallar" while standing in Haber Kanallar, which parses as
             * "go to Haber Kanallar" and does the opposite. Where back *should* go depends
             * on how the filter was chosen — a provider category came from the category
             * list, a genre or language came from a discovery row — so it is derived
             * rather than fixed, and the current filter is stated separately as plain text.
             */
            <p className="library__crumb">
              <button
                type="button"
                onClick={() => (filter.categoryId ? setFilter(null) : backToRows())}
              >
                ← {filter.categoryId ? 'Tüm Kategoriler' : 'Keşfet'}
              </button>
              <span className="library__crumbNow">{filter.label}</span>
            </p>
          )}

          {!filter && (
            <nav className="rail" aria-label="Kategoriler">
              {categories.data?.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setFilter({ categoryId: c.id, label: c.name })}
                >
                  {c.name} <span className="rail__count">{c.itemCount}</span>
                </button>
              ))}
            </nav>
          )}

          {categories.error && <ErrorBox message={categories.error} onRetry={categories.reload} />}
          {grid.error && <ErrorBox message={grid.error} />}

          {!grid.loading && grid.items.length === 0 && (applied || filter) && (
            /* The way out is the point. A dead end with no button is where people press
               Back and lose the whole screen rather than just the filter. */
            <Empty
              title="Sonuç yok"
              action={
                <button type="button" onClick={backToRows}>
                  Keşfet'e dön
                </button>
              }
            >
              {applied
                ? `“${applied}” bu kütüphanede bulunamadı.`
                : 'Bu filtreyle eşleşen içerik yok.'}
            </Empty>
          )}

          {grid.items.length > 0 && (
            <>
              <p className="library__total">{grid.total} sonuç</p>
              <ItemGrid
                items={grid.items}
                hasMore={grid.hasMore}
                loading={grid.loading}
                onLoadMore={grid.loadMore}
              />
            </>
          )}

          {/* Only for the *first* page — a skeleton under an existing grid would read as
              the results having been replaced rather than extended. */}
          {grid.loading && grid.items.length === 0 && (
            <SkeletonGrid poster={library !== 'live'} />
          )}
        </>
      )}
    </main>
  );
}
